/**
 * Help assistant model card (Admin Dashboard).
 *
 * Shows which Claude model the Help chat uses and whether the weekly watcher
 * has seen a newer Opus. Switch / Roll back go through the
 * `adminSetSupportChatModel` callable, which sends a real test message first
 * and only saves when it passes (the config doc is not writable from the
 * browser). A failed roll-back test can be overridden after a confirm.
 */

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';

const DEFAULT_MODEL = 'claude-opus-5';

interface SetModelResult {
  success: boolean;
  changed: boolean;
  model: string;
  previousModel: string | null;
  fallbacks: boolean;
  tested: boolean;
  testError?: string;
  watch?: { newestOpusId: string | null; newerAvailable: boolean; currentRetired: boolean };
}

type Mode = 'switch' | 'rollback' | 'check';

export function HelpModelCard() {
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [previousModel, setPreviousModel] = useState<string | null>(null);
  const [newestOpusId, setNewestOpusId] = useState<string | null>(null);
  const [newerAvailable, setNewerAvailable] = useState(false);
  const [currentRetired, setCurrentRetired] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [customId, setCustomId] = useState('');
  const [busy, setBusy] = useState<Mode | null>(null);
  const [note, setNote] = useState('');
  const [isError, setIsError] = useState(false);

  const load = async () => {
    try {
      const [cfgSnap, watchSnap] = await Promise.all([
        getDoc(doc(db, 'config', 'supportChat')),
        getDoc(doc(db, 'config', 'supportChatModelWatch')),
      ]);
      const cfg = cfgSnap.data() ?? {};
      const modelInUse = typeof cfg.model === 'string' && cfg.model ? cfg.model : DEFAULT_MODEL;
      setModel(modelInUse);
      setPreviousModel(typeof cfg.previousModel === 'string' && cfg.previousModel ? cfg.previousModel : null);
      const watch = watchSnap.data() ?? {};
      // The watch doc describes the model that was in use when the check ran. After
      // a switch it is stale until the next check — never pin its "retired" flag
      // on a different model. "Newer seen" is NOT gated: the `suggested` line
      // below already hides it when the newest id is the one in use, and gating
      // it would hide the Switch button after a switch to a custom id.
      setNewestOpusId(typeof watch.newestOpusId === 'string' ? watch.newestOpusId : null);
      setNewerAvailable(watch.newerAvailable === true);
      setCurrentRetired(watch.currentRetired === true && watch.currentModel === modelInUse);
      setLastChecked(watch.lastCheckedAt?.toDate ? watch.lastCheckedAt.toDate() : null);
    } catch (err) {
      console.error('[HelpModelCard] load failed', err);
    }
  };

  useEffect(() => { load(); }, []);

  const call = async (mode: Mode, payload: Record<string, unknown>) => {
    setBusy(mode);
    setNote(mode === 'check' ? 'Checking Anthropic’s model list…' : 'Sending a test message to the model… (up to a minute)');
    setIsError(false);
    try {
      // The callable allows 180s (two test attempts); Firebase's client default is 70s.
      const fn = httpsCallable<Record<string, unknown>, SetModelResult>(
        functions, 'adminSetSupportChatModel', { timeout: 190_000 },
      );
      const { data } = await fn({ mode, ...payload });

      if (!data.success && mode === 'rollback' && !payload.confirmUntested) {
        const force = window.confirm(
          `The test message to "${previousModel ?? DEFAULT_MODEL}" failed:\n\n${data.testError ?? 'unknown error'}\n\n` +
          'Roll back anyway, without a passing test? This is recorded in the audit log as untested.',
        );
        if (force) {
          await call('rollback', { confirmUntested: true });
          return;
        }
      }

      if (!data.success) {
        setIsError(true);
        setNote(`Not switched — the test message failed: ${data.testError ?? 'unknown error'}`);
      } else if (mode === 'check') {
        setNote(
          data.watch?.currentRetired
            ? 'The model in use is no longer available — switch now.'
            : data.watch?.newerAvailable
              ? `Newer model seen: ${data.watch.newestOpusId}`
              : 'No newer Opus model found.',
        );
      } else {
        setNote(
          `${data.changed ? 'Now using' : 'Already using'} ${data.model}` +
          `${data.tested ? ' — test passed' : ' — NOT tested'}` +
          `${data.fallbacks ? '' : ' (safety fallback off: the API rejected it)'}. Takes effect within a minute.`,
        );
        setCustomId('');
      }
      await load();
    } catch (err) {
      setIsError(true);
      setNote(`Error: ${err instanceof Error ? err.message : 'request failed'}`);
    } finally {
      setBusy(null);
    }
  };

  const suggested = newerAvailable && newestOpusId && newestOpusId !== model ? newestOpusId : null;

  return (
    <section style={{ marginBottom: '32px', padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
      <h3 style={{ marginTop: 0 }}>Help assistant model</h3>
      <p style={{ opacity: 0.8, marginTop: '4px', marginBottom: '8px', fontSize: '0.9rem' }}>
        In use: <strong>{model}</strong>
        {previousModel && <span> &middot; previous: {previousModel}</span>}
        {lastChecked && <span> &middot; last checked {lastChecked.toLocaleDateString()}</span>}
      </p>
      {currentRetired && (
        <p style={{ color: 'var(--red)', marginTop: 0, marginBottom: '8px', fontSize: '0.9rem' }}>
          This model is no longer available. Chat is running on the built-in default until you switch.
        </p>
      )}
      {suggested && (
        <p style={{ color: 'var(--gold)', marginTop: 0, marginBottom: '8px', fontSize: '0.9rem' }}>
          Newer model seen: <strong>{suggested}</strong>
        </p>
      )}
      <p style={{ opacity: 0.7, marginTop: 0, marginBottom: '12px', fontSize: '0.85rem' }}>
        A weekly check emails you when a newer Opus appears. Switching sends one real test message first
        (about 8 cents) and only saves if it passes. Every switch is written to the admin audit log.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {suggested && (
          <button type="button" className="btn btn-primary" disabled={busy !== null}
            onClick={() => call('switch', { model: suggested })}>
            {busy === 'switch' ? 'Testing…' : `Switch to ${suggested}`}
          </button>
        )}
        <button type="button" className="btn btn-secondary" disabled={busy !== null || !previousModel}
          onClick={() => call('rollback', {})}>
          {busy === 'rollback' ? 'Testing…' : previousModel ? `Roll back to ${previousModel}` : 'Roll back'}
        </button>
        <button type="button" className="btn btn-secondary" disabled={busy !== null}
          onClick={() => call('check', {})}>
          {busy === 'check' ? 'Checking…' : 'Check for new models now'}
        </button>
        <input
          type="text"
          value={customId}
          onChange={e => setCustomId(e.target.value.trim())}
          placeholder="or type a model id"
          disabled={busy !== null}
          style={{ padding: '8px', minWidth: '200px' }}
        />
        {/* The model in use is allowed on purpose: re-testing it is the only way
            to record "safety fallback off" when the API rejects that setting. */}
        <button type="button" className="btn btn-secondary" disabled={busy !== null || !customId}
          onClick={() => call('switch', { model: customId })}>
          {customId && customId === model ? 'Re-test this model' : 'Test & switch'}
        </button>
      </div>
      {note && (
        <p style={{ marginTop: '12px', marginBottom: 0, fontSize: '0.9rem', color: isError ? 'var(--red)' : 'var(--green)' }}>
          {note}
        </p>
      )}
    </section>
  );
}
